import { Controller } from '@hotwired/stimulus'

export interface AttachEvent extends Event {
  detail: {
    files: FileList
  }
}

export default class DropzoneController extends Controller<HTMLElement> {
  declare inputTarget: HTMLInputElement

  static targets = ['input']

  connect() {
    this.element.addEventListener('dragenter', this.#handleDragEnter.bind(this))
    this.element.addEventListener('dragleave', this.#handleDragLeave.bind(this))
    this.element.addEventListener('dragover', this.#handleDragOver.bind(this))
    this.element.addEventListener('drop', this.#handleDrop.bind(this))
    this.inputTarget.addEventListener('change', this.#handleInput.bind(this))
  }

  disconnect() {
    this.element.removeEventListener('dragenter', this.#handleDragEnter.bind(this))
    this.element.removeEventListener('dragleave', this.#handleDragLeave.bind(this))
    this.element.removeEventListener('dragover', this.#handleDragOver.bind(this))
    this.element.removeEventListener('drop', this.#handleDrop.bind(this))
    this.inputTarget.removeEventListener('change', this.#handleInput.bind(this))
  }

  // Public methods

  cancelDrag = () => {
    this.element.classList.remove('dragover', 'dragover-invalid')
  }

  // Private methods

  #attachFiles(files: FileList) {
    this.dispatch('attach', { detail: { files } })
    this.inputTarget.value = ''
  }

  #handleDragEnter(event: DragEvent) {
    if (!event.dataTransfer) return

    const items = Array.from(event.dataTransfer.items)
    const files = items.filter(item => item.kind === 'file')
    const noFiles = files.length === 0
    const tooManyFiles = !this.inputTarget.multiple && files.length > 1

    if (noFiles || tooManyFiles) {
      this.element.classList.add('dragover-invalid')
    } else {
      this.element.classList.add('dragover')
    }
  }

  #handleDragLeave(event: DragEvent) {
    const draggingOnZoneElem = event.relatedTarget === this.element
    const draggingInZoneElem = this.element.contains(event.relatedTarget as Node)

    if (draggingOnZoneElem || draggingInZoneElem) return

    this.element.classList.remove('dragover', 'dragover-invalid')
  }

  #handleDragOver(event: DragEvent) {
    event.preventDefault() // Required to allow drop
  }

  #handleDrop(event: DragEvent) {
    event.preventDefault() // Required to handle drop

    if (!event.dataTransfer) return

    this.element.classList.remove('dragover')

    const { files } = event.dataTransfer
    const hasFiles = files?.length > 0
    const hasTooManyFiles = !this.inputTarget.multiple && files?.length > 1

    if (hasFiles && !hasTooManyFiles) this.#attachFiles(files)
  }

  #handleInput(event: Event) {
    const files = (event.target as HTMLInputElement).files

    if (files && files.length > 0) this.#attachFiles(files)
  }
}
